import { useState, useCallback, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { SideNav } from './SideNav.jsx';
import { TopBar } from './TopBar.jsx';
import { AiCopilotDrawer } from './AiCopilotDrawer.jsx';
import { PrimeCopilotLauncher } from './PrimeCopilotLauncher.jsx';
import { CopilotControllerProvider } from './CopilotController.jsx';
import { PageMetaProvider } from './PageMetaContext.jsx';
import { useCurrentUser } from './CurrentUserContext.jsx';
import { ToastHost } from '../components/inspectors/ToastHost.jsx';
import { toast } from '../components/index.js';
import { RichTooltipProvider } from '../components/richTooltips/RichTooltipProvider.jsx';
import { useNotificationPoll } from '../lib/useNotificationPoll.js';
import { FeedbackMarkupProvider } from '../features/feedbackMarkup/FeedbackMarkupProvider.jsx';
import { FeedbackMarkupOverlay } from '../features/feedbackMarkup/FeedbackMarkupOverlay.jsx';
import { reviewToolsEnabled } from '../features/feedbackMarkup/reviewToolsCapability.js';
import { applyDisplayPreferences } from '../features/me/displayPreferences.js';
import { useOperatorMode } from '../lib/operatorMode.js';
import { recordActivityEvent } from '../lib/activityCapture.js';
import { FirstRunWelcome } from '../features/onboarding/FirstRunWelcome.jsx';
import { TourProvider } from '../features/tours/TourProvider.jsx';
import { TourPanel } from '../features/tours/TourPanel.jsx';
import { ActivityCaptureProvider } from './ActivityCaptureProvider.jsx';
import { PageHeaderDefaultActionsContext } from '../components/primitives.jsx';
import { AboutThisPageButton } from '../features/help/AboutThisPageButton.jsx';
import { useIsMobileShell } from '../lib/useIsMobileShell.js';
import './appShell.css';
// PRIME-MOB-UX-001 (AC-05): the mobile adaptation primitives (.m-stack /
// .m-cards / .m-scroll + the shared touch-target token) load with the shell so
// they are available app-wide for Part B to apply to page content.
import '../styles/mobileAdaptations.css';

// Every routed page header (the shared primitive PageHeader and the shell-level
// one) carries the About this page control now that the topbar no longer hosts
// it (PRIME-SHELL-UX-001). Provided here so the primitive stays feature-free.
const PAGE_HEADER_DEFAULT_ACTIONS = <AboutThisPageButton />;

const COPILOT_LAPTOP_MAX_WIDTH = 1540;
const COPILOT_TIGHT_WIDTH = 1280;
const COPILOT_MIN = 380;
const COPILOT_LAPTOP_MIN = 340;
const COPILOT_TIGHT_MIN = 300;
const COPILOT_MAX_RATIO = 0.45;
const COPILOT_DEFAULT = 400;

// localStorage keys for shell-level UI preferences that should persist across
// reloads (otherwise the demo menu's "Turn rich tooltips off" toggle, the
// rail collapse state, the copilot drawer width, etc. all reset to defaults
// every page load — which violates user expectations).
const LS_KEYS = {
  tooltipsDisabled: 'dha_prime__tooltips_disabled',
  railExpanded: 'dha_prime__rail_expanded',
  copilotOpen: 'dha_prime__copilot_open',
  copilotWidth: 'dha_prime__copilot_width',
  demoMode: 'dha_prime__demo_mode',
};

const RESEED_SUCCESS_KEY = 'dha_prime__demo_reseed_success';

function readBool(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return raw === 'true';
  } catch {
    return fallback;
  }
}
function readNumber(key, fallback) {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  } catch {
    return fallback;
  }
}
function writeLS(key, value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    /* storage may be unavailable in private mode — fall back silently */
  }
}

function copilotMinForViewport() {
  if (typeof window === 'undefined') return COPILOT_MIN;
  if (window.innerWidth <= COPILOT_TIGHT_WIDTH) return COPILOT_TIGHT_MIN;
  return window.innerWidth <= COPILOT_LAPTOP_MAX_WIDTH ? COPILOT_LAPTOP_MIN : COPILOT_MIN;
}

function copilotDefaultForViewport() {
  if (typeof window === 'undefined') return COPILOT_DEFAULT;
  if (window.innerWidth <= COPILOT_TIGHT_WIDTH) return COPILOT_TIGHT_MIN;
  if (window.innerWidth <= COPILOT_LAPTOP_MAX_WIDTH) {
    return Math.max(
      COPILOT_LAPTOP_MIN,
      Math.min(COPILOT_MIN, Math.round(window.innerWidth * 0.25)),
    );
  }
  return COPILOT_DEFAULT;
}

export function AppShell() {
  const { user, loading, setUnreadNotificationCount } = useCurrentUser();
  const location = useLocation();
  const navigate = useNavigate();

  // PRIME-MOB-UX-001: mobile-shell mode + off-canvas nav drawer. The rail
  // becomes a drawer the hamburger opens; the shell owns the open/close state,
  // the scrim, the body-scroll lock, and returns focus to the hamburger on the
  // affordance-driven closes. SideNav renders the SAME nav content — one
  // component, two presentations.
  const isMobileShell = useIsMobileShell();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const hamburgerRef = useRef(null);
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  // Affordance-driven close (scrim tap / Escape / close button) returns focus to
  // the hamburger so keyboard users are never stranded (locked decision 2).
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Defer so focus lands after the drawer is marked closed/inert.
    requestAnimationFrame(() => hamburgerRef.current?.focus());
  }, []);
  // Route navigation closes the drawer WITHOUT stealing focus back to the
  // hamburger — the destination page should own focus.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);
  // Leaving mobile mode (rotate to landscape / resize up) tears the drawer down.
  useEffect(() => {
    if (!isMobileShell) setDrawerOpen(false);
  }, [isMobileShell]);
  // Lock body scroll while the drawer overlays content (locked decision 2).
  useEffect(() => {
    if (!(isMobileShell && drawerOpen)) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isMobileShell, drawerOpen]);

  // Re-apply persisted display preferences (Settings → /me/preferences,
  // stored on the user record and returned by GET /me) whenever the signed-in
  // user changes, so compact density / reduced motion survive reloads and
  // persona switches (AC-ME-4).
  useEffect(() => {
    applyDisplayPreferences(user?.preferences);
  }, [user]);
  const suppressCopilotOnFeedbackWorkspace = location.pathname.startsWith('/feedback/workspace');
  // Shell-level UI preferences are hydrated from localStorage on first mount
  // so toggles like "Turn rich tooltips off", rail collapse, and the copilot
  // drawer width survive a page reload. Monday demo defaults still apply
  // when no prior value is stored.
  const [railExpanded, setRailExpanded] = useState(() => readBool(LS_KEYS.railExpanded, true));
  // PRIME Copilot is COLLAPSED by default (05 §2, Top-25 #11): the open
  // drawer steals ~380px on every screen. An explicit user toggle is
  // persisted to localStorage and always wins over this default for the rest
  // of the session and across reloads.
  const [copilotOpen, setCopilotOpen] = useState(() => readBool(LS_KEYS.copilotOpen, false));
  const [demoMode, setDemoMode] = useState(() => readBool(LS_KEYS.demoMode, false));
  // Rich tooltips are ON by default (WP3): the plain-language hover definitions
  // are a core part of the SME onboarding experience, so a first-time evaluator
  // gets them unless they explicitly turned them off (that stored preference
  // still wins on every later load).
  const [tooltipsDisabled, setTooltipsDisabled] = useState(() =>
    readBool(LS_KEYS.tooltipsDisabled, false),
  );
  const [copilotWidth, setCopilotWidth] = useState(() =>
    Math.max(
      copilotMinForViewport(),
      readNumber(LS_KEYS.copilotWidth, copilotDefaultForViewport()),
    ),
  );
  const [resizing, setResizing] = useState(false);

  // Feature G — live mention/notification pop. Poll the lightweight unread
  // count for the signed-in user; push it into CurrentUserContext (drives the
  // TopBar bell badge + its increase pulse) and toast newly-arrived mentions
  // with a deep link. `navigate` is captured here because the toast host has no
  // router context of its own.
  const canPollNotifications = !loading && !!user;
  const handleNotificationArrival = useCallback(
    ({ latest }) => {
      if (!latest) return;
      const kindLabel =
        latest.kind === 'action_handoff'
          ? 'New handoff'
          : latest.kind === 'action_mention'
            ? 'New mention'
            : 'New notification';
      toast.show({
        tone: 'info',
        title: kindLabel,
        body: latest.title || latest.body || 'You have a new notification.',
        durationMs: 6000,
        action: latest.actionRoute
          ? { label: 'Open', onClick: () => navigate(latest.actionRoute) }
          : undefined,
      });
    },
    [navigate],
  );
  useNotificationPoll({
    enabled: canPollNotifications,
    onCount: setUnreadNotificationCount,
    onArrival: handleNotificationArrival,
  });
  // Operator mode (P1-E8-1 / AC-SHELL-4): the presentation overlay is demo
  // chrome, so a persisted demoMode preference only takes effect while an
  // operator session is active. The stored preference itself is preserved.
  const { enabled: operatorModeEnabled } = useOperatorMode();
  const effectiveDemoMode = demoMode && operatorModeEnabled;

  useEffect(() => writeLS(LS_KEYS.railExpanded, railExpanded), [railExpanded]);
  useEffect(() => writeLS(LS_KEYS.copilotOpen, copilotOpen), [copilotOpen]);
  useEffect(() => writeLS(LS_KEYS.demoMode, demoMode), [demoMode]);
  useEffect(() => writeLS(LS_KEYS.tooltipsDisabled, tooltipsDisabled), [tooltipsDisabled]);
  useEffect(() => writeLS(LS_KEYS.copilotWidth, copilotWidth), [copilotWidth]);
  // PRIME-SHELL-UX-002 (AC-02): the rich-tooltips preference now persists to the
  // PRIME profile alongside the other display preferences, so — like compact
  // density / reduced motion — it is reapplied on sign-in, on any device. The
  // legacy `dha_prime__tooltips_disabled` localStorage key is still honored: it
  // seeds the initial `tooltipsDisabled` state above and remains the live
  // in-session seam (the write-through effect keeps it in sync); the profile
  // value only overrides it when the signed-in user's stored preferences carry
  // an explicit boolean. `user` only changes reference on a real /me refresh
  // (sign-in / persona switch), so this never fights an in-session toggle.
  useEffect(() => {
    const stored = user?.preferences?.tooltipsDisabled;
    if (typeof stored === 'boolean') setTooltipsDisabled(stored);
  }, [user]);
  // The Settings Display card (canonical user-facing home for this preference)
  // writes the same localStorage seam and fires this event, so the live shell
  // and RichTooltipProvider reflect a rich-tooltips change immediately without a
  // reload. The shell stays the single owner of the live state.
  useEffect(() => {
    function onTooltipsPreferenceChanged(event) {
      const next = event?.detail?.tooltipsDisabled;
      if (typeof next === 'boolean') setTooltipsDisabled(next);
    }
    window.addEventListener('prime:tooltips-preference-changed', onTooltipsPreferenceChanged);
    return () =>
      window.removeEventListener('prime:tooltips-preference-changed', onTooltipsPreferenceChanged);
  }, []);
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(RESEED_SUCCESS_KEY);
      if (!raw) return;
      window.sessionStorage.removeItem(RESEED_SUCCESS_KEY);
      const payload = JSON.parse(raw);
      const shortHash = String(payload?.baselineHash || '').slice(0, 8);
      toast.success({
        title: 'Baseline data restored',
        body: shortHash ? `Baseline hash ${shortHash}` : 'Baseline data is back in place.',
      });
    } catch {
      /* ignore malformed session marker */
    }
  }, []);
  useEffect(() => {
    const syncResponsiveCopilotWidth = () => {
      const min = copilotMinForViewport();
      const defaultWidth = copilotDefaultForViewport();
      const max = Math.round(window.innerWidth * COPILOT_MAX_RATIO);
      setCopilotWidth((current) => {
        const target =
          window.innerWidth <= COPILOT_LAPTOP_MAX_WIDTH
            ? defaultWidth
            : Math.max(COPILOT_MIN, current);
        return Math.max(min, Math.min(max, target));
      });
    };
    syncResponsiveCopilotWidth();
    window.addEventListener('resize', syncResponsiveCopilotWidth);
    return () => window.removeEventListener('resize', syncResponsiveCopilotWidth);
  }, []);

  const toggleRail = useCallback(() => setRailExpanded((v) => !v), []);
  const openCopilot = useCallback(() => {
    recordActivityEvent({
      eventType: 'ui',
      eventName: 'ui.drawer.open',
      source: 'shell',
      targetType: 'drawer',
      targetId: 'prime-copilot',
      targetLabel: 'PRIME Copilot',
    });
    setCopilotOpen(true);
  }, []);
  const closeCopilot = useCallback(() => {
    recordActivityEvent({
      eventType: 'ui',
      eventName: 'ui.drawer.close',
      source: 'shell',
      targetType: 'drawer',
      targetId: 'prime-copilot',
      targetLabel: 'PRIME Copilot',
    });
    setCopilotOpen(false);
  }, []);
  const toggleDemoMode = useCallback(() => setDemoMode((v) => !v), []);
  const toggleTooltipsDisabled = useCallback(() => setTooltipsDisabled((v) => !v), []);

  // Resize handler: while dragging the drawer's left edge, recompute width
  // from the cursor's distance from the viewport's right edge. The shell
  // root receives the new value as a CSS custom property, so both the
  // drawer width and the main's padding-right reflow together in real time.
  const dragStartRef = useRef(null);
  const startResize = useCallback(
    (e) => {
      dragStartRef.current = { startX: e.clientX, startWidth: copilotWidth };
      setResizing(true);
      e.preventDefault();
    },
    [copilotWidth],
  );

  useEffect(() => {
    if (!resizing) return undefined;
    const onMove = (e) => {
      const delta = dragStartRef.current.startX - e.clientX;
      const next = Math.round(dragStartRef.current.startWidth + delta);
      const max = Math.round(window.innerWidth * COPILOT_MAX_RATIO);
      setCopilotWidth(Math.max(copilotMinForViewport(), Math.min(max, next)));
    };
    const onUp = () => setResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [resizing]);

  const cls = [
    'app-shell',
    railExpanded && 'rail-expanded',
    copilotOpen && 'copilot-open',
    resizing && 'copilot-resizing',
    isMobileShell && 'mobile-shell',
    isMobileShell && drawerOpen && 'drawer-open',
  ]
    .filter(Boolean)
    .join(' ');

  const copilotVisible = copilotOpen && !suppressCopilotOnFeedbackWorkspace;

  // Inline style sets --copilot-width on the shell root so descendants pick
  // up the live value without re-rendering each card. Falls back to 0px
  // when the drawer is closed so the main reclaims the full content area.
  const shellStyle = {
    '--copilot-width': copilotVisible ? `${copilotWidth}px` : '0px',
  };

  return (
    <TourProvider>
      <PageMetaProvider>
        <ActivityCaptureProvider>
          <FeedbackMarkupProvider>
            <RichTooltipProvider disabled={tooltipsDisabled}>
              <CopilotControllerProvider
                open={copilotOpen}
                onOpen={openCopilot}
                onClose={closeCopilot}
              >
                <div
                  className={cls}
                  data-testid="app-shell"
                  data-route={location.pathname}
                  data-persona={user?.id || 'anon'}
                  style={shellStyle}
                >
                  {/* Scrim: closes the drawer on tap and dims content beneath.
                      Rendered only in the mobile shell while the drawer is open,
                      so it never intercepts desktop interaction. */}
                  {isMobileShell && drawerOpen ? (
                    <div
                      className="app-rail-scrim"
                      data-testid="app-rail-scrim"
                      onClick={closeDrawer}
                      aria-hidden="true"
                    />
                  ) : null}
                  <SideNav
                    expanded={isMobileShell ? true : railExpanded}
                    onToggle={toggleRail}
                    mobileDrawer={isMobileShell}
                    drawerOpen={drawerOpen}
                    onRequestClose={closeDrawer}
                  />
                  <TopBar
                    demoMode={effectiveDemoMode}
                    onToggleDemoMode={toggleDemoMode}
                    tooltipsDisabled={tooltipsDisabled}
                    onToggleTooltipsDisabled={toggleTooltipsDisabled}
                    mobile={isMobileShell}
                    onOpenDrawer={openDrawer}
                    hamburgerRef={hamburgerRef}
                  />
                  <main className="app-main" data-testid="app-main" role="main" aria-live="polite">
                    <PageHeaderDefaultActionsContext.Provider value={PAGE_HEADER_DEFAULT_ACTIONS}>
                      <Outlet />
                    </PageHeaderDefaultActionsContext.Provider>
                  </main>
                  <AiCopilotDrawer
                    open={copilotVisible}
                    onClose={closeCopilot}
                    demoMode={effectiveDemoMode}
                    onResizeStart={startResize}
                    resizing={resizing}
                  />
                  {!copilotOpen && !suppressCopilotOnFeedbackWorkspace ? (
                    <PrimeCopilotLauncher onOpen={openCopilot} />
                  ) : null}
                  {/* PRIME-ACT-UX-001 (AC-07): the right-side Activity rail and its
                      10s global-audit poller are retired. Personal history now
                      lives at /activity (Me section). The useActivityRail.js
                      registry + activityRail.push seam remain intact so feature
                      pages keep calling push() harmlessly. */}
                  <ToastHost />
                  {reviewToolsEnabled() ? <FeedbackMarkupOverlay /> : null}
                  {/* First-run SME welcome (WP3). Self-gates on route + onboarding
                      state, so mounting it here unconditionally is safe. */}
                  <FirstRunWelcome />
                  {/* Guided-tour side panel (WP4). Renders nothing unless a tour is
                      active; the surrounding TourProvider owns the tour state. */}
                  <TourPanel />
                </div>
              </CopilotControllerProvider>
            </RichTooltipProvider>
          </FeedbackMarkupProvider>
        </ActivityCaptureProvider>
      </PageMetaProvider>
    </TourProvider>
  );
}
